import { Component, createRef } from "react"

/* Modal */
import Modal from "react-modal"

/* Moment */
import moment from "moment"
import "moment/locale/ru"

/* Icons */
import { LoadingOutlined } from "@ant-design/icons"

/* REST API */
import { get, status as changeStatus } from "../../api/Booking"

/* Constants */
import { env } from "../../constants"

/* Components */
import { Avatar, Icon } from "../../components"

/* Helpers */
import { utils } from "../../helpers"

/* Widgets */
import { toast, confirm } from "../common"
import TimeModal from "./TimeModal"
import CompleteModal from "./CompleteModal"

/* Shared */
import { ACTIONS, CHANNELS, LOG_STATUSES, LOG_TYPES, MESSAGE_STATUSES, bookingIcon, line, priceOf, statusOf } from "./shared"

moment.locale("ru")
Modal.setAppElement("#root")


/* The questions the panel asks before it changes the state of a visit by hand */
const QUESTIONS = {
    confirm: { title: "Принять запись?", text: "Клиент получит уведомление о подтверждении", confirmText: "Да, принять", danger: false },
    cancel: { title: "Отменить запись?", text: "Клиент получит уведомление об отмене", confirmText: "Да, отменить", danger: true },
    missed: { title: "Клиент не пришёл?", text: "Запись будет отмечена как несостоявшаяся", confirmText: "Да, отметить", danger: true },
    reopen: { title: "Вернуть запись в работу?", text: "Запись снова станет принятой, оплата будет очищена", confirmText: "Да, вернуть", danger: false }
}


/* Widget Details Modal — the card of one visit, a panel which slides in from the right.
   It is opened by the reference: this._details.current.open(booking), so neither the page
   nor the tab of the point keeps anything about it in its own state */
class DetailsModal extends Component {

    constructor() {
        super()

        this.state = {

            visible: false,

            /* The row of the list — it is drawn at once, while the card is still loading */
            booking: null,

            status: "loading",
            data: null,

            acting: ""
        }

        /* The form of the time and the form of the completion are opened by the reference */
        this._time = createRef()
        this._complete = createRef()
    }

    /* Open the card of the visit */
    open = booking => {
        this.setState({ visible: true, booking, status: "loading", data: null, acting: "" }, () => this.load())
    }

    /* Close the card */
    close = () => {
        this.setState({ visible: false, booking: null, data: null })
    }

    /* Load the card of the visit */
    load = () => {

        /* Fields */
        const { booking } = this.state

        /* Check Data */
        if (!booking) {
            return
        }

        get({ id: booking.id }).then(response => {
            if (response && response.status === 200) {
                this.setState({ status: "ok", data: response.data })
            }
            else {
                this.setState({ status: "error" })
            }
        }).catch(() => this.setState({ status: "error" }))
    }

    /* Something changed — the card reads itself again and the list behind it too */
    reload = () => {

        const { onChanged } = this.props

        this.load()

        if (onChanged) {
            onChanged()
        }
    }

    /* One action of the card. Завершить asks for the amount in a form of its own,
       the rest only ask whether it is really meant */
    act = action => {

        /* Fields */
        const { data, acting } = this.state

        /* The previous action is still on its way */
        if (acting.length > 0 || !data) {
            return
        }

        /* The completion is written down together with the amount which was really charged */
        if (action === "complete") {
            this._complete.current.open(data.booking)
            return
        }

        const question = QUESTIONS[action]

        confirm({
            title: question.title,
            text: data.notified && action !== "reopen" ? "Клиент уже получал уведомление по этой записи, повторное отправлено не будет" : question.text,
            confirmText: question.confirmText,
            cancelText: "Нет",
            danger: question.danger
        }).then(confirmed => {

            if (!confirmed) {
                return
            }

            this.setState({ acting: action })

            changeStatus({ id: data.booking.id, action }).then(response => {

                this.setState({ acting: "" })

                if (response && response.status === 200) {
                    toast.success("Статус записи изменён")
                    this.reload()
                }
                else {
                    toast.error("Не удалось изменить статус записи")
                }
            }).catch(() => {
                this.setState({ acting: "" })
                toast.error("Не удалось изменить статус записи")
            })
        })
    }

    /* The head of the card — the point, the number of the visit and its state */
    _head = () => {

        /* Fields */
        const { booking, data } = this.state

        const card = data ? data.booking : booking
        const image = card.organizationImage ? `${env.mediapoint}organizations/${card.organizationImage}` : null
        const state = statusOf(card.status)
        const channel = utils.check(CHANNELS[card.channel], card.channel)

        return (
            <div className="booking-details-head">

                <div className="booking-details-head-top">

                    <Avatar className="booking-details-avatar" src={image} alt={card.organization}>
                        {bookingIcon()}
                    </Avatar>

                    <div className="booking-details-title">

                        <div className="booking-details-name">Запись №{utils.check(card.number, card.id)}</div>

                        <div className="booking-details-subtitle">
                            <span>{utils.check(card.organization, "Организация удалена")}</span>
                            {channel ? <span>{channel}</span> : null}
                            <span>создана {moment(card.createdAt).format("DD.MM.YYYY HH:mm")}</span>
                        </div>

                        <div className="booking-details-tags">
                            <div className={`tag ${state.color}`}>{state.text}</div>
                            <div className="tag grey">{moment(card.reservedTime).format("DD MMMM, HH:mm")}</div>
                            <div className="tag orange">{utils.convertor(priceOf(card), "₸", 1)}</div>
                        </div>

                    </div>

                    <div onClick={this.close} className="booking-details-close">
                        <Icon name="close" size={11} width={2.2} />
                    </div>

                </div>

            </div>
        )
    }

    /* The buttons which act on the visit */
    _actions = () => {

        /* Fields */
        const { data, acting } = this.state

        const current = parseInt(data.booking.status)

        return (
            <div className="booking-details-actions">

                <div onClick={() => this._time.current.open(data.booking)} className="booking-details-action">
                    <Icon name="edit" size={16} />
                    <span>Изменить время</span>
                </div>

                {ACTIONS.filter(action => action.from.indexOf(current) > -1).map(action =>
                    <div
                        key={action.key}
                        onClick={() => this.act(action.key)}
                        className={`booking-details-action ${action.primary ? "primary" : ""} ${action.danger ? "danger" : ""} ${acting === action.key ? "disabled" : ""}`}
                    >
                        {acting === action.key ? <LoadingOutlined /> : <Icon name={action.icon} size={16} />}
                        <span>{action.text}</span>
                    </div>
                )}

            </div>
        )
    }

    /* One event of the history */
    _log = item => {

        /* What exactly happened */
        const text = utils.check(LOG_TYPES[item.type], "Изменение записи")

        /* What it turned into — a state, a time or a plain value */
        let value = ""

        if (item.type === "status") {
            value = utils.check(LOG_STATUSES[item.value], item.value)
        }
        else if (item.type === "time") {
            value = `${moment(item.fromValue).format("DD.MM HH:mm")} → ${moment(item.toValue).format("DD.MM HH:mm")}`
        }
        else if (item.type === "payment") {
            value = utils.convertor(item.toValue, "₸", 1)
        }
        else if (item.fromValue || item.toValue) {
            value = `${utils.check(item.fromValue, "—")} → ${utils.check(item.toValue, "—")}`
        }

        return (
            <div key={`${item.id}`} className="booking-details-log">

                <div className="booking-details-log-line" />

                <div className="booking-details-log-body">
                    <div className="booking-details-log-title">{text}</div>
                    {value.length > 0 && <div className="booking-details-log-value">{value}</div>}
                    <div className="booking-details-log-text">
                        {moment(item.createdAt).format("DD.MM.YYYY HH:mm")} · {item.member ? item.member : "Администратор"}
                    </div>
                </div>

            </div>
        )
    }

    /* The content of the card */
    _content = () => {

        /* Fields */
        const { status, data } = this.state

        if (status === "loading") {
            return (
                <div className="booking-details-loading">
                    <LoadingOutlined />
                </div>
            )
        }

        if (status !== "ok" || !data) {
            return (
                <div className="booking-details-loading">
                    <Icon name="alert" size={38} width={1.5} />
                    <p>Не удалось загрузить запись</p>
                    <div onClick={() => this.setState({ status: "loading" }, () => this.load())} className="booking-details-button">Повторить</div>
                </div>
            )
        }

        const { booking, services, logs, messages } = data

        const ends = moment(booking.reservedTime).add(parseInt(booking.sessionTime) ? parseInt(booking.sessionTime) : 0, "minutes")

        return (
            <div className="booking-details-body">

                {this._actions()}

                <div className="booking-details-columns">

                    <div className="booking-details-column">

                        <div className="booking-details-section">

                            <div className="booking-details-section-text">Запись</div>

                            {line("Организация", utils.check(booking.organization, "—"))}
                            {line("Адрес", booking.organizationAddress)}
                            {line("Телефон организации", booking.organizationPhone)}
                            {line("Мастер", utils.check(booking.member, "—"))}
                            {line("Дата и время", `${moment(booking.reservedTime).format("DD.MM.YYYY HH:mm")} — ${ends.format("HH:mm")}`)}
                            {line("Длительность", `${utils.check(booking.sessionTime, "—")} мин`)}
                            {line("Канал", utils.check(CHANNELS[booking.channel], booking.channel))}
                            {line("Создана", moment(booking.createdAt).format("DD.MM.YYYY HH:mm"))}

                        </div>

                        <div className="booking-details-section">

                            <div className="booking-details-section-text">Клиент</div>

                            {line("Имя", utils.check(booking.user, "Без имени"))}
                            {line("Телефон", utils.check(booking.phone, "—"), "accent")}
                            {line("Аккаунт", booking.userID ? utils.check(booking.account, `#${booking.userID}`) : "Гость")}
                            {line("Email", booking.accountEmail)}

                        </div>

                        <div className="booking-details-section">

                            <div className="booking-details-section-text">Оплата</div>

                            {line("По прайсу", utils.convertor(booking.total, "₸", 1, " ", "—"))}
                            {line("Итог мастера", booking.endTotal ? utils.convertor(booking.endTotal, "₸", 1) : null)}
                            {line("Фактически оплачено", booking.finalPrice !== null && booking.finalPrice !== undefined ? utils.convertor(booking.finalPrice, "₸", 1) : "Не указано", "accent")}
                            {line("Способ оплаты", booking.paymentType === "mixed" ? "Смешанная оплата" : utils.check(booking.paymentMethod, "—"))}
                            {line("Доля мастера", booking.share !== null && booking.share !== undefined ? `${booking.share}%` : null)}
                            {line("Скидка", parseInt(booking.hasDiscount) === 1 ? "Применена акционная цена" : null)}

                        </div>

                    </div>

                    <div className="booking-details-column">

                        <div className="booking-details-section">

                            <div className="booking-details-section-text">Услуги</div>

                            {services.length === 0 && <div className="booking-details-empty">Услуги записи не сохранились</div>}

                            {services.map(service =>
                                <div key={`${service.id}`} className="booking-details-row">

                                    <div className="booking-details-row-main">
                                        <div className="booking-details-row-title">{service.name}</div>
                                        <div className="booking-details-row-text">
                                            {utils.check(service.sessionTime, "—")} мин
                                            {parseInt(service.active) === 0 ? " · в архиве" : ""}
                                        </div>
                                    </div>

                                    <div className="booking-details-row-side">
                                        <div className="booking-details-row-price">
                                            {parseInt(service.isFixedPrice) === 0 && service.endPrice
                                                ? `${utils.convertor(service.price, "", 1)}—${utils.convertor(service.endPrice, "₸", 1)}`
                                                : utils.convertor(service.price, "₸", 1)
                                            }
                                        </div>
                                    </div>

                                </div>
                            )}

                        </div>

                        <div className="booking-details-section">

                            <div className="booking-details-section-text">История изменений</div>

                            {logs.length === 0 && <div className="booking-details-empty">История пуста</div>}

                            {logs.map(item => this._log(item))}

                        </div>

                        <div className="booking-details-section">

                            <div className="booking-details-section-text">Сообщения клиенту</div>

                            {messages.length === 0 && <div className="booking-details-empty">Сообщений не было</div>}

                            {messages.map(message => {

                                const state = MESSAGE_STATUSES[message.status] ? MESSAGE_STATUSES[message.status] : MESSAGE_STATUSES.sent

                                return (
                                    <div key={`${message.id}`} className="booking-details-row">

                                        <div className="booking-details-row-main">
                                            <div className="booking-details-row-title">{utils.check(message.template, message.type)}</div>
                                            <div className="booking-details-row-text">
                                                {moment(message.createdAt).format("DD.MM.YYYY HH:mm")} · {utils.check(message.phone, "—")}
                                            </div>
                                        </div>

                                        <div className="booking-details-row-side">
                                            <div className={`tag ${state.color}`}>{state.text}</div>
                                        </div>

                                    </div>
                                )
                            })}

                        </div>

                    </div>

                </div>

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { visible, booking } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={this.close}
                className="booking-details-modal"
                overlayClassName="booking-details-overlay"
                closeTimeoutMS={300}
            >
                {booking &&
                    <div className="booking-details">

                        {this._head()}
                        {this._content()}

                        {/* Draw Time */}
                        <TimeModal ref={this._time} onSaved={this.reload} />

                        {/* Draw Complete */}
                        <CompleteModal ref={this._complete} onSaved={this.reload} />

                    </div>
                }
            </Modal>
        )
    }
}

export default DetailsModal
